import { useState, useCallback } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import {
  FixedBooking,
  FixedBookingStatus,
  CreateFixedBookingRequest,
  UpdateFixedBookingRequest,
} from '../../types/venue';

export interface FixedBookingFilters {
  status?: FixedBookingStatus;
  venueId?: string;
}

export const useFixedBookings = () => {
  const [fixedBookings, setFixedBookings] = useState<FixedBooking[]>([]);
  const [selectedFixedBooking, setSelectedFixedBooking] = useState<FixedBooking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all fixed bookings for the owner
  const fetchFixedBookings = useCallback(async (filters?: FixedBookingFilters) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.venueId) params.append('venueId', filters.venueId);

    const endpoint = `/fixed-bookings${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiRequest<FixedBooking[]>(endpoint);

    if (response.data) {
      setFixedBookings(response.data);
    } else {
      setError(response.error || 'Error al cargar turnos fijos');
    }

    setLoading(false);
  }, []);

  // Fetch a single fixed booking
  const fetchFixedBooking = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);

    const response = await apiRequest<FixedBooking>(`/fixed-bookings/${id}`);

    if (response.data) {
      setSelectedFixedBooking(response.data);
      return { success: true, fixedBooking: response.data };
    }

    setError(response.error || 'Error al cargar turno fijo');
    setLoading(false);
    return { success: false, error: response.error };
  }, []);

  // Create a new fixed booking
  const createFixedBooking = useCallback(async (data: CreateFixedBookingRequest) => {
    const response = await apiRequest<FixedBooking>('/fixed-bookings', {
      method: 'POST',
      body: data,
    });

    if (response.data) {
      setFixedBookings((prev) => [...prev, response.data!]);
      return { success: true, fixedBooking: response.data };
    }

    // Don't set page-level error — the modal displays it
    return { success: false, error: response.error };
  }, []);

  // Update a fixed booking
  const updateFixedBooking = useCallback(
    async (id: string, data: UpdateFixedBookingRequest) => {
      const response = await apiRequest<FixedBooking>(`/fixed-bookings/${id}`, {
        method: 'PUT',
        body: data,
      });

      if (response.data) {
        setFixedBookings((prev) =>
          prev.map((fb) => (fb.id === id ? response.data! : fb))
        );
        if (selectedFixedBooking?.id === id) {
          setSelectedFixedBooking(response.data);
        }
        return { success: true, fixedBooking: response.data };
      }

      // Don't set page-level error — the modal displays it
      return { success: false, error: response.error };
    },
    [selectedFixedBooking]
  );

  // Pause a fixed booking
  const pauseFixedBooking = useCallback(async (id: string) => {
    const response = await apiRequest<{ message: string; fixedBooking: FixedBooking }>(
      `/fixed-bookings/${id}/pause`,
      { method: 'PUT' }
    );

    if (response.data) {
      setFixedBookings((prev) =>
        prev.map((fb) => (fb.id === id ? response.data!.fixedBooking : fb))
      );
      return { success: true, fixedBooking: response.data.fixedBooking };
    }

    return { success: false, error: response.error };
  }, []);

  // Resume a fixed booking
  const resumeFixedBooking = useCallback(async (id: string) => {
    const response = await apiRequest<{ message: string; fixedBooking: FixedBooking }>(
      `/fixed-bookings/${id}/resume`,
      { method: 'PUT' }
    );

    if (response.data) {
      setFixedBookings((prev) =>
        prev.map((fb) => (fb.id === id ? response.data!.fixedBooking : fb))
      );
      return { success: true, fixedBooking: response.data.fixedBooking };
    }

    return { success: false, error: response.error };
  }, []);

  // Cancel (delete) a fixed booking
  const cancelFixedBooking = useCallback(
    async (id: string, cancelFutureBookings: boolean = false) => {
      const endpoint = `/fixed-bookings/${id}${cancelFutureBookings ? '?cancelFutureBookings=true' : ''}`;
      const response = await apiRequest<{ message: string; fixedBooking: FixedBooking }>(
        endpoint,
        { method: 'DELETE' }
      );

      if (response.data) {
        setFixedBookings((prev) =>
          prev.map((fb) => (fb.id === id ? response.data!.fixedBooking : fb))
        );
        return { success: true };
      }

      return { success: false, error: response.error };
    },
    []
  );

  // Generate bookings from a fixed booking
  const generateBookings = useCallback(
    async (id: string, weeksAhead: number = 4) => {
      const response = await apiRequest<{
        message: string;
        generatedCount: number;
        bookings: { id: string; bookingDate: string; startTime: string; endTime: string }[];
      }>(`/fixed-bookings/${id}/generate`, {
        method: 'POST',
        body: { weeksAhead },
      });

      if (response.data) {
        return {
          success: true,
          generatedCount: response.data.generatedCount,
          bookings: response.data.bookings,
        };
      }

      return { success: false, error: response.error };
    },
    []
  );

  // Get fixed bookings grouped by day
  const getFixedBookingsByDay = useCallback(() => {
    const grouped: Record<number, FixedBooking[]> = {
      0: [], // Sunday
      1: [], // Monday
      2: [], // Tuesday
      3: [], // Wednesday
      4: [], // Thursday
      5: [], // Friday
      6: [], // Saturday
    };

    fixedBookings
      .filter((fb) => fb.status === 'ACTIVE' || fb.status === 'PAUSED')
      .forEach((fb) => {
        grouped[fb.dayOfWeek].push(fb);
      });

    // Sort each day by start time
    Object.keys(grouped).forEach((day) => {
      grouped[Number(day)].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });

    return grouped;
  }, [fixedBookings]);

  // Get active fixed bookings count
  const activeCount = fixedBookings.filter((fb) => fb.status === 'ACTIVE').length;
  const pausedCount = fixedBookings.filter((fb) => fb.status === 'PAUSED').length;

  return {
    // State
    fixedBookings,
    selectedFixedBooking,
    loading,
    error,

    // Setters
    setSelectedFixedBooking,

    // Actions
    fetchFixedBookings,
    fetchFixedBooking,
    createFixedBooking,
    updateFixedBooking,
    pauseFixedBooking,
    resumeFixedBooking,
    cancelFixedBooking,
    generateBookings,

    // Helpers
    getFixedBookingsByDay,

    // Stats
    activeCount,
    pausedCount,
    totalCount: fixedBookings.length,
  };
};
